/**
 * The three moments this product is allowed to make something of.
 *
 * Nob is a doorman, not a party. Almost everything it does is a small
 * administrative act and should read as one — an acknowledgement line that
 * clears itself, and no more. But three things are genuinely events, and a
 * product that treats "somebody you wanted to hear from just got through" the
 * same as "limit saved" is lying about which one mattered.
 *
 * THERE WERE FOUR. A topped-up balance was one of them, and ruling R-0916-1
 * deleted the balance; a bought reset never inherited the confetti, and the
 * paid reset is itself deleted now (ruling R-0923-3 (1)).
 *
 * WHAT IS NOT ON THIS LIST, and never should be: anything that merely
 * SUCCEEDED. A saved setting, a sent turn, a copied link. The test for
 * belonging here is whether the person would tell somebody about it.
 *
 * A module store rather than context, for the same reason `lib/shell.ts` is
 * one: it is fired from inside network callbacks that are nowhere near a
 * provider, and there is only ever one of it on screen.
 */
import { useSyncExternalStore } from "react";

export type CelebrateKind = "through" | "plan" | "door";

export type Celebrate = {
  id: string;
  kind: CelebrateKind;
  /** The stamp. Short, and shouted only because a stamp is. */
  stamp: string;
  title: string;
  body: string;
};

let current: Celebrate | null = null;
let counter = 0;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

export function celebrate(event: Omit<Celebrate, "id">): void {
  counter += 1;
  current = { ...event, id: `cel-${counter}` };
  announce();
}

export function clearCelebrate(): void {
  if (!current) return;
  current = null;
  announce();
}

export function useCelebrate(): Celebrate | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );
}

/** Test-only, and the sign-out path. */
export function resetCelebrate(): void {
  current = null;
  counter = 0;
  announce();
}
